import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Link2, Loader2, User } from 'lucide-react';
import { SeverityBadge } from '../ui/SeverityBadge';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { cn, formatRelativeTime, getRiskScoreColor } from '../../lib/utils';
import { updateIncidentStatus } from '../../services';
import { sortIncidents, getIncidentKey } from './incidentQueueSort';
import type { EnrichedIncident } from '../../data/types';

const statusVariantMap = {
  new: 'orange',
  acknowledged: 'blue',
  investigating: 'purple',
  closed: 'green',
  reopened: 'red',
} as const;

type AckState = 'idle' | 'pending' | 'done' | 'error';

interface QuickAcknowledgeButtonProps {
  incident: EnrichedIncident;
  onAcknowledged: (updated: EnrichedIncident) => void;
  className?: string;
}

/**
 * One-tap acknowledge for `new` incidents. Awaits the server response (no
 * optimistic update) and hands the updated incident back to the caller, so
 * the status badge flips in place once the API confirms.
 */
export function QuickAcknowledgeButton({
  incident,
  onAcknowledged,
  className,
}: QuickAcknowledgeButtonProps) {
  const [state, setState] = useState<AckState>('idle');

  const handleAcknowledge = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (state === 'pending' || state === 'done') return;

    setState('pending');
    const result = await updateIncidentStatus(
      incident.tenantId,
      incident.incidentId,
      'acknowledged'
    );

    if (result.success) {
      setState('done');
      onAcknowledged(result.data);
    } else {
      setState('error');
    }
  };

  return (
    <Button
      size="sm"
      variant={state === 'error' ? 'destructive' : 'primary'}
      onClick={handleAcknowledge}
      disabled={state === 'pending'}
      className={cn('px-4', className)}
      aria-label={`Acknowledge incident ${incident.incidentId}`}
    >
      {state === 'pending' ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Check className="w-4 h-4" />
      )}
      {state === 'error' ? 'Failed — retry' : state === 'done' ? 'Acknowledged' : 'Acknowledge'}
    </Button>
  );
}

interface IncidentQueueCardListProps {
  incidents: EnrichedIncident[];
  onIncidentUpdated: (updated: EnrichedIncident) => void;
}

/**
 * Mobile replacement for IncidentTable — one card per incident in the same
 * default queue order, with a one-tap Acknowledge on new incidents.
 */
export function IncidentQueueCardList({
  incidents,
  onIncidentUpdated,
}: IncidentQueueCardListProps) {
  const navigate = useNavigate();
  const sortedIncidents = sortIncidents(incidents, 'queue', 'desc');

  return (
    <div className="space-y-3 pb-24">
      {sortedIncidents.map((incident) => (
        <div
          key={getIncidentKey(incident)}
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/incidents/${incident.tenantId}/${incident.incidentId}`)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(`/incidents/${incident.tenantId}/${incident.incidentId}`);
            }
          }}
          className="bg-bg-secondary border border-border-default rounded-xl p-4 active:bg-bg-tertiary transition-colors cursor-pointer"
        >
          {/* Badges + risk */}
          <div className="flex items-center gap-2 flex-wrap">
            <SeverityBadge severity={incident.severity} />
            <Badge variant={statusVariantMap[incident.platformStatus] ?? 'default'}>
              {incident.platformStatus}
            </Badge>
            {(incident.newAlertCount ?? 0) > 0 && (
              <Badge variant="blue" className="shrink-0 whitespace-nowrap">
                +{incident.newAlertCount} new alert{incident.newAlertCount === 1 ? '' : 's'}
              </Badge>
            )}
            {incident.playbookId && (
              <span title="Linked to playbook">
                <Link2 className="w-3.5 h-3.5 text-text-muted" />
              </span>
            )}
            <span className="ml-auto">
              {incident.aiEnrichment?.riskScore !== undefined ? (
                <span
                  className="font-mono font-bold"
                  style={{ color: getRiskScoreColor(incident.aiEnrichment.riskScore) }}
                >
                  {incident.aiEnrichment.riskScore.toFixed(1)}
                </span>
              ) : (
                <span className="text-text-secondary">—</span>
              )}
            </span>
          </div>

          {/* Title */}
          <div className="mt-2 text-sm font-medium text-text-primary line-clamp-2">
            {incident.title}
          </div>

          {/* Tenant + id */}
          <div className="mt-1 text-xs text-text-secondary font-mono">
            {incident.tenantDisplayName || incident.tenantId} · {incident.incidentId}
          </div>

          {/* Footer: assignee, age, acknowledge */}
          <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-border-default">
            <div className="flex items-center gap-3 min-w-0 text-sm text-text-secondary">
              {incident.assignedToName ? (
                <span className="flex items-center gap-1.5 min-w-0">
                  <User className="w-3.5 h-3.5 shrink-0 text-text-muted" />
                  <span className="truncate">{incident.assignedToName}</span>
                </span>
              ) : (
                <span>Unassigned</span>
              )}
              <span className="font-mono text-xs text-text-muted shrink-0">
                {formatRelativeTime(incident.createdTime)}
              </span>
            </div>
            {incident.platformStatus === 'new' && (
              <QuickAcknowledgeButton
                incident={incident}
                onAcknowledged={onIncidentUpdated}
                className="shrink-0"
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
